import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import format from 'string-template';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import {
  IconButton,
  Button,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
  ToggleButton,
} from '@mui/material';
import {
  DatePicker,
  TimePicker,
} from '@mui/lab';
import {
  IonSpinner,
} from '@ionic/react';

import {
  combineDateAndTimeWithUTCOffset,
  DateFormat,
  isToday,
} from '../../../../../../../utils/date';
import BroadcastMessageRequest, {
  BroadcastMessageRequestStatus,
} from '../../../../../../../Model/BroadcastMessageRequest';
import useToast from '../../../../../../hooks/useToast';
import useStorage, { StorageProcessState } from '../../../../../../../hooks/useStorage';
import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import { getUTCOffset } from '../../../../../../../utils/time';
import { pathPlaceholder, storagePaths } from '../../../../../../../utils/firebasePaths';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../../../utils/files';
import UserContext from '../../../../../../../context/UserContext';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import FileAttachmentContainer from '../../../../../../components/AttachmentsContainer';
import { InfoTag } from '../../../../../../../components/Tags';
import Coach from '../../../../../../../Model/Coach';
import { filterUnsupportedFiles, getUnsupportedFileTypes } from '../../../../../../../utils/file';
import ForceSendHelperText from '../ForceSendHelperText';

import text from '../../text.json';
import { DELIVERY_MODE } from '../../constants';
import {
  StyledTextField,
  OptionsRow,
  HelperText,
  SectionLabel,
  PersonalizeChipsContainer,
  PersonalizeChip,
  RecipientRow,
  RecipientInfo,
  RecipientName,
  DateSelectorContainer,
  DateSelectorField,
  SendBroadcastButton,
  StyledListContainer,
  SearchBar,
  StyledToggleButtonGroup,
  StyledAlert,
} from '../../styles';
import ChatContext from '../../../../../../context/ChatContext';
import {
  ItemContainer,
  MessageDate,
  MessageText,
  ActionsContainer,
  InfoContainer,
  EditContainer,
  InfoText,
  MessageContent,
  LabelContent,
  InfoWrapper,
  IconContainer,
} from './styles';
import texts from './texts.json';

const ItemStatus = {
  INITIAL: 'INITIAL',
  EDITING: 'EDITING',
  DELETING: 'DELETING',
};

// remove uuid from file name
const sanitizeFileName = (encodedName) => encodedName
  .replace(/-[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}/i, '');

const ScheduledMessageItem = ({
  broadcastMessageRequestDoc,
  onMessageDelete,
}) => {
  const {
    message,
    sendAt,
    attachmentsRefs,
    recipients,
    week,
    messageSendDay,
  } = broadcastMessageRequestDoc;
  const {
    sendBroadcastMessage,
    setIsBroadcastModalOpen,
  } = useContext(ChatContext);

  const { coachDoc } = useContext(ExternalCoachContext);
  const timezoneOffset = getUTCOffset(coachDoc.timeZone || moment.tz.guess());

  const [sendDate, setSendDate] = useState(moment(sendAt).utcOffset(timezoneOffset));
  const [sendTime, setSendTime] = useState(moment(sendAt).utcOffset(timezoneOffset));
  const [messageText, setMessageText] = useState(message);
  const [itemStatus, setItemStatus] = useState(ItemStatus.INITIAL);
  const [attachments, setAttachments] = useState(attachmentsRefs.map((ref) => ({
    name: sanitizeFileName(ref.split('/').pop()),
    url: ref,
    isUploaded: true,
  })));
  const [isUploading, setIsUploading] = useState(false);
  const { showToast } = useToast();
  const { userId } = useContext(UserContext);
  const { uploadAttachments, deleteAttachments } = useStorage();
  const isComponentMountedRef = useComponentMounted();

  // States for recipient selection
  const [allRecipients, setAllRecipients] = useState([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState(
    () => new Set((recipients || []).map((r) => r.id)),
  );
  const [isReady, setIsReady] = useState(false);
  const [userSearchValue, setUserSearchValue] = useState('');
  const [shouldForceSend, setShouldForceSend] = useState(broadcastMessageRequestDoc.shouldForceSend || false);
  const [deliveryMode, setDeliveryMode] = useState(DELIVERY_MODE.SCHEDULE);

  // Load the full client list once
  useEffect(() => {
    if (isReady) return;
    const init = async () => {
      const usersListOfCoach = await Coach.getBroadcastMessageUsers(userId);
      const sortedUsersListOfCoach = [...usersListOfCoach].sort((a, b) => (a.name > b.name ? 1 : -1));
      const clientList = sortedUsersListOfCoach.map((user) => ({
        id: user.id,
        userDoc: user,
      }));
      if (isComponentMountedRef.current) {
        setAllRecipients(clientList);
        setIsReady(true);
      }
    };
    init();
  }, [isReady, userId, isComponentMountedRef]);

  const filterUsersByName = useCallback((users) => {
    if (!userSearchValue) {
      return users;
    }
    return users.filter((user) => user.userDoc.name.toLowerCase().includes(userSearchValue.toLowerCase()));
  }, [userSearchValue]);

  const handleCheckChange = useCallback((checked, id) => {
    setSelectedRecipientIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const onChangeSearch = useCallback((e) => {
    setUserSearchValue(e.target.value);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setItemStatus(ItemStatus.INITIAL);
    setDeliveryMode(DELIVERY_MODE.SCHEDULE);
  }, []);

  const uploadMessageAttachments = useCallback(async () => {
    // adding already uploaded files to the array
    const fileAttachmentRefs = attachments.reduce((arr, current) => {
      if (current.isUploaded) {
        arr.push(current.url);
      }
      return arr;
    }, []);

    // filtering files that are not uploaded yet
    const filesToUpload = attachments.filter((file) => !file.isUploaded);
    const storagePath = format(storagePaths.COACH_BROADCAST_MESSAGES_ATTACHMENTS, {
      [pathPlaceholder.COACH_ID]: userId,
    });

    setIsUploading(true);
    try {
      const result = await uploadAttachments(filesToUpload, storagePath);

      let storageTaskFailed = false;
      const successfulUploads = [];
      result.forEach((res) => {
        if (res.state === StorageProcessState.SUCCESS) {
          successfulUploads.push(res.fileRef);
        } else {
          storageTaskFailed = true;
        }
      });
      if (storageTaskFailed) {
        // If some upload fails, we should stop the saving process
        // and delete the files that were uploaded (if there is any)
        if (fileAttachmentRefs.length) {
          const filesToDelete = successfulUploads.map((ref) => ({ storagePath: ref }));
          await deleteAttachments(filesToDelete);
        }
      }
      fileAttachmentRefs.push(...successfulUploads);
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
      return { fileAttachmentRefs, storageTaskFailed };
    } catch (error) {
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
      Sentry.captureException(error, {
        extra: {
          coachId: userId,
        },
      });
      // Returning this to handle error messages from the caller function
      return { storageTaskFailed: true };
    }
  }, [
    attachments,
    userId,
    uploadAttachments,
    deleteAttachments,
    isComponentMountedRef,
  ]);

  const getAttachmentRefs = useCallback(async () => {
    let fileAttachments = [];
    let error = false;
    if (attachments.length) {
      const { storageTaskFailed, fileAttachmentRefs } = await uploadMessageAttachments();
      if (storageTaskFailed) {
        error = true;
      }
      fileAttachments = fileAttachmentRefs;
    }
    return {
      fileAttachments,
      error,
    };
  }, [
    attachments,
    uploadMessageAttachments,
  ]);

  const sendNow = useCallback(async () => {
    const additionalFields = {};
    const { fileAttachments, error } = await getAttachmentRefs();
    if (error) {
      showToast(texts.uploadError, { error: true });
      return;
    }
    additionalFields.attachmentsRefs = fileAttachments;

    const mappedRecipients = allRecipients
      .filter((r) => selectedRecipientIds.has(r.id) && r.userDoc)
      .map((r) => ({ id: r.id, userDoc: r.userDoc }));

    await sendBroadcastMessage(mappedRecipients, messageText, additionalFields, shouldForceSend);
    await broadcastMessageRequestDoc.updateStatus(BroadcastMessageRequestStatus.IN_PROGRESS);
    showToast(texts.broadcastMessageSendNow);
    setIsBroadcastModalOpen(false);
  }, [
    broadcastMessageRequestDoc,
    getAttachmentRefs,
    messageText,
    allRecipients,
    selectedRecipientIds,
    sendBroadcastMessage,
    showToast,
    setIsBroadcastModalOpen,
    shouldForceSend,
  ]);

  const onSave = useCallback(async () => {
    const { fileAttachments, error } = await getAttachmentRefs();
    if (error) {
      showToast(texts.uploadError, { error: true });
      return;
    }
    // Convert to UTC after adding date and time
    const newSendAt = combineDateAndTimeWithUTCOffset(sendDate, sendTime, timezoneOffset).valueOf();
    await broadcastMessageRequestDoc.update({
      message: messageText,
      sendAt: newSendAt,
      attachmentsRefs: fileAttachments,
      shouldForceSend,
      recipients: allRecipients
        .filter((recipient) => selectedRecipientIds.has(recipient.id) && recipient.userDoc)
        .map(({ id, userDoc }) => ({
          id,
          firstName: userDoc?.firstName || 'Client',
        })),
    });

    setAttachments(fileAttachments.map((ref) => ({
      name: sanitizeFileName(ref.split('/').pop()),
      url: ref,
      isUploaded: true,
    })));
    setSendTime(moment(newSendAt).local().utcOffset(timezoneOffset));
    setSendDate(moment(newSendAt).local().utcOffset(timezoneOffset));
    setItemStatus(ItemStatus.INITIAL);
  }, [
    broadcastMessageRequestDoc,
    messageText,
    sendDate,
    sendTime,
    getAttachmentRefs,
    showToast,
    timezoneOffset,
    allRecipients,
    selectedRecipientIds,
    shouldForceSend,
  ]);

  const onDelete = useCallback(async () => {
    await broadcastMessageRequestDoc.delete();
    onMessageDelete(broadcastMessageRequestDoc.id);
    setItemStatus(ItemStatus.INITIAL);
  }, [
    broadcastMessageRequestDoc,
    onMessageDelete,
  ]);

  // If the selected date is today, then the user cannot select a time in the past
  const minTime = useMemo(() => (
    isToday(moment(sendDate).utcOffset(timezoneOffset)) ? moment().utcOffset(timezoneOffset) : null
  ), [
    sendDate,
    timezoneOffset,
  ]);

  const onFilesSelected = useCallback((event) => {
    const { files } = event.target;
    if (files.length) {
      const filesArray = [...files];

      // Filter out unsupported files
      const { hasUnsupportedFiles, filteredFiles } = filterUnsupportedFiles(filesArray);
      if (hasUnsupportedFiles) {
        const fileTypes = getUnsupportedFileTypes();
        showToast(format(text.unsupportedFileWarning, { fileTypes }), { error: true });
      }

      if (!filteredFiles.length) {
        return;
      }

      const validFiles = filteredFiles.every((file) => file.size < MAX_FILE_UPLOAD_SIZE);
      if (validFiles) {
        const formattedFiles = filteredFiles.map((file) => {
          const lastDotIndex = file.name.lastIndexOf('.');
          return {
            dataUrl: URL.createObjectURL(file),
            format: file.name.slice(lastDotIndex + 1),
            fileName: file.name.slice(0, lastDotIndex),
            fileType: file.type,
            name: file.name,
            isUploaded: false,
          };
        });
        setAttachments((prev) => [...prev, ...formattedFiles]);
      } else {
        showToast(format(texts.fileSizeError, {
          maxFileSize: MAX_FILE_UPLOAD_SIZE_MB,
        }), { error: true });
      }
    }
  }, [
    showToast,
  ]);

  const onFileRemove = useCallback(async (index) => {
    const resultAttachments = [...attachments];
    resultAttachments.splice(index, 1);
    setAttachments(resultAttachments);
  }, [
    attachments,
  ]);

  const renderRecipientList = useCallback(() => {
    if (!isReady) {
      return <IonSpinner />;
    }

    const nameFilteredRecipients = filterUsersByName(allRecipients).sort((a, b) => {
      const aSelected = selectedRecipientIds.has(a.id);
      const bSelected = selectedRecipientIds.has(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
    if (allRecipients.length > 0) {
      return (
        <>
          <SearchBar
            size="small"
            value={userSearchValue}
            onChange={onChangeSearch}
            placeholder={texts.searchUser}
          />
          <StyledListContainer>
            {nameFilteredRecipients.map((recipient) => (
              <RecipientRow
                key={recipient.id}
                onClick={() => !week && handleCheckChange(
                  !selectedRecipientIds.has(recipient.id),
                  recipient.id,
                )}
              >
                <RecipientInfo>
                  <RecipientName>{recipient.userDoc.name}</RecipientName>
                </RecipientInfo>
                <Checkbox
                  checked={selectedRecipientIds.has(recipient.id)}
                  onChange={() => !week && handleCheckChange(!selectedRecipientIds.has(recipient.id), recipient.id)}
                  disabled={!!week}
                  size="small"
                />
              </RecipientRow>
            ))}
          </StyledListContainer>
        </>
      );
    }

    return <RecipientRow><RecipientName>{texts.noActiveClients}</RecipientName></RecipientRow>;
  }, [
    isReady,
    filterUsersByName,
    allRecipients,
    userSearchValue,
    onChangeSearch,
    handleCheckChange,
    week,
    selectedRecipientIds,
  ]);

  const renderItemContent = () => {
    switch (itemStatus) {
      case ItemStatus.EDITING:
        return (
          <EditContainer>
            <SectionLabel>{texts.recipientsLabel}</SectionLabel>
            {!!week && <HelperText>{texts.journeyRecipientsNote}</HelperText>}
            {renderRecipientList()}
            <SectionLabel>{text.messageLabel}</SectionLabel>
            <PersonalizeChipsContainer>
              <PersonalizeChip
                label={text.personalizeClientName}
                onClick={() => setMessageText((prevText) => `${prevText}{clientName}`)}
                size="small"
                variant="outlined"
              />
            </PersonalizeChipsContainer>
            <StyledTextField
              placeholder={text.enterMessage}
              value={messageText}
              fullWidth
              multiline
              rows={4}
              onChange={(e) => setMessageText(e.target.value)}
            />
            <SectionLabel>{text.deliveryLabel}</SectionLabel>
            <StyledToggleButtonGroup
              value={deliveryMode}
              exclusive
              onChange={(_, value) => value && setDeliveryMode(value)}
              size="small"
            >
              <ToggleButton value={DELIVERY_MODE.SEND_NOW} disabled={!!week}>{text.sendNow}</ToggleButton>
              <ToggleButton value={DELIVERY_MODE.SCHEDULE}>{text.schedule}</ToggleButton>
            </StyledToggleButtonGroup>
            {deliveryMode === DELIVERY_MODE.SCHEDULE && (
              <>
                {!!coachDoc.timeZone && (
                  <StyledAlert severity="warning">{text.scheduleWarningMessage}</StyledAlert>
                )}
                <DateSelectorContainer>
                  <DatePicker
                    label={text.selectDate}
                    value={sendDate}
                    onChange={(newValue) => setSendDate(newValue)}
                    minDate={moment().utcOffset(timezoneOffset)}
                    maxDate={moment().utcOffset(timezoneOffset).add(3, 'months')}
                    renderInput={(params) => <DateSelectorField {...params} size="small" />}
                    disabled={!!week}
                  />
                  <TimePicker
                    label={text.selectTime}
                    value={sendTime}
                    onChange={(newValue) => setSendTime(newValue)}
                    minTime={minTime}
                    minutesStep={5}
                    renderInput={(params) => <DateSelectorField {...params} size="small" />}
                    disabled={!!week}
                  />
                </DateSelectorContainer>
              </>
            )}
            <SectionLabel>{text.optionsLabel}</SectionLabel>
            <OptionsRow>
              <FormControlLabel
                control={(
                  <Switch
                    checked={shouldForceSend}
                    onChange={() => setShouldForceSend((prev) => !prev)}
                    size="small"
                  />
                )}
                label={text.forceSendLabel}
              />
            </OptionsRow>
            <ForceSendHelperText isForceSendEnabled={shouldForceSend} />
            <FileAttachmentContainer
              fileAttachments={attachments}
              onFileRemoved={onFileRemove}
              onFilesSelected={onFilesSelected}
              isUploading={isUploading}
            />
            <ActionsContainer>
              <Button
                onClick={handleCancelEdit}
                color="info"
              >
                {texts.button.cancel}
              </Button>
              <SendBroadcastButton
                onClick={deliveryMode === DELIVERY_MODE.SCHEDULE ? onSave : sendNow}
              >
                {deliveryMode === DELIVERY_MODE.SCHEDULE ? texts.button.save : texts.button.send}
              </SendBroadcastButton>
            </ActionsContainer>
          </EditContainer>
        );
      case ItemStatus.DELETING:
        return (
          <InfoContainer>
            <InfoText>{texts.deleteConfirm}</InfoText>
          </InfoContainer>
        );
      case ItemStatus.INITIAL:
      default:
        return (
          <InfoWrapper>
            <InfoContainer>
              <MessageContent>
                <MessageDate>
                  {moment(sendDate).format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT)}
                </MessageDate>
                <MessageText>{messageText}</MessageText>
              </MessageContent>
              {!!week && (
                <LabelContent>
                  <InfoTag>
                    {messageSendDay
                      ? format(texts.clientJourneyWeekMsgWithDay, { week, day: messageSendDay })
                      : format(texts.clientJourneyWeekMsg, { week })}
                  </InfoTag>
                </LabelContent>
              )}
            </InfoContainer>
            {attachments.length !== 0 && (
              <IconContainer>
                <Tooltip title={texts.tooltip.attachments}>
                  <AttachFileIcon />
                </Tooltip>
              </IconContainer>
            )}
          </InfoWrapper>
        );
    }
  };

  const renderActionButtons = () => {
    switch (itemStatus) {
      case ItemStatus.EDITING:
        return <></>;
      case ItemStatus.DELETING:
        return (
          <ActionsContainer>
            <Button
              onClick={() => setItemStatus(ItemStatus.INITIAL)}
              color="info"
            >
              {texts.button.cancel}
            </Button>
            <Button
              onClick={onDelete}
              color="warning"
            >
              {texts.button.delete}
            </Button>
          </ActionsContainer>
        );
      case ItemStatus.INITIAL:
      default:
        return (
          <ActionsContainer>
            <Tooltip title={texts.tooltip.edit}>
              <IconButton
                onClick={() => setItemStatus(ItemStatus.EDITING)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={texts.tooltip.delete}>
              <IconButton
                onClick={() => setItemStatus(ItemStatus.DELETING)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </ActionsContainer>
        );
    }
  };

  return (
    <ItemContainer>
      {renderItemContent()}
      {renderActionButtons()}
    </ItemContainer>
  );
};

ScheduledMessageItem.propTypes = {
  broadcastMessageRequestDoc: PropTypes.instanceOf(BroadcastMessageRequest).isRequired,
  onMessageDelete: PropTypes.func.isRequired,
};

export default observer(ScheduledMessageItem);
